import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { GraduationCap, IndianRupee, Calendar, Percent, Baby, Download } from "lucide-react";

export default function ChildEducationCalculatorPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/calculator/child-education`;
  const schemas = [
    webpageSchema(
      "Child Education Calculator | Future Cost & SIP Planner | ExpertAdvise.in",
      "Plan for your child's higher education cost. Calculate future education expenses and monthly savings required with inflation adjustments.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Calculators", url: `${baseUrl}/calculators` },
      { name: "Child Education Calculator", url: pageUrl },
    ]),
  ];
  const [childAge, setChildAge] = useState(5);
  const [educationAge, setEducationAge] = useState(18);
  const [currentCost, setCurrentCost] = useState(20);
  const [inflation, setInflation] = useState(10);
  const [returnRate, setReturnRate] = useState(12);
  const [existingSavings, setExistingSavings] = useState(3);

  const results = useMemo(() => {
    const yearsLeft = educationAge - childAge;
    const futureCost = currentCost * 100000 * Math.pow(1 + inflation / 100, yearsLeft);
    const existingValue = existingSavings * 100000 * Math.pow(1 + returnRate / 100, yearsLeft);
    const amountNeeded = Math.max(0, futureCost - existingValue);
    const monthlySIP = amountNeeded * (returnRate / 100 / 12) / (Math.pow(1 + returnRate / 100 / 12, yearsLeft * 12) - 1);
    return {
      yearsLeft,
      futureCost: Math.round(futureCost),
      amountNeeded: Math.round(amountNeeded),
      monthlySIP: Math.round(monthlySIP),
      existingValue: Math.round(existingValue),
    };
  }, [childAge, educationAge, currentCost, inflation, returnRate, existingSavings]);

  const formatCurrency = useCallback((val: number) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} Lakhs`;
    return `₹${val.toLocaleString()}`;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Child Education Calculator | Future Cost & SIP Planner | ExpertAdvise.in"
        description="Plan for your child's higher education cost. Calculate future education expenses and monthly savings required with inflation adjustments."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />
      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-amber-50 text-amber-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Goal Calculator</span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">Child Education Calculator</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">Plan for your child's higher education — engineering, medical, MBA, or overseas studies. Calculate the future cost and how much to save monthly.</p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-5">Your Details</h3>
                <div className="space-y-5">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Baby className="w-4 h-4 text-primary-500" />Child's Current Age</label>
                    <input type="range" min="0" max="15" value={childAge} onChange={(e) => setChildAge(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>0</span><span className="font-semibold text-primary-700">{childAge} Years</span><span>15</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><GraduationCap className="w-4 h-4 text-primary-500" />Age When Needed</label>
                    <input type="range" min="15" max="25" value={educationAge} onChange={(e) => setEducationAge(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>15</span><span className="font-semibold text-primary-700">{educationAge} Years</span><span>25</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Current Education Cost (₹ Lakhs)</label>
                    <input type="range" min="5" max="100" step="5" value={currentCost} onChange={(e) => setCurrentCost(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹5L</span><span className="font-semibold text-primary-700">₹{currentCost}L</span><span>₹100L</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Percent className="w-4 h-4 text-primary-500" />Education Inflation (%)</label>
                    <input type="range" min="5" max="15" step="0.5" value={inflation} onChange={(e) => setInflation(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>5%</span><span className="font-semibold text-primary-700">{inflation}%</span><span>15%</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><Calendar className="w-4 h-4 text-primary-500" />Expected Return (%)</label>
                    <input type="range" min="8" max="15" step="0.5" value={returnRate} onChange={(e) => setReturnRate(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>8%</span><span className="font-semibold text-primary-700">{returnRate}%</span><span>15%</span></div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2"><IndianRupee className="w-4 h-4 text-primary-500" />Existing Savings (₹ Lakhs)</label>
                    <input type="range" min="0" max="50" step="1" value={existingSavings} onChange={(e) => setExistingSavings(Number(e.target.value))} className="w-full accent-primary-600 mb-2" />
                    <div className="flex justify-between text-sm text-slate-500"><span>₹0</span><span className="font-semibold text-primary-700">₹{existingSavings}L</span><span>₹50L</span></div>
                  </div>
                </div>
              </div>
              <button onClick={() => alert("PDF report generation will be enabled after backend integration.")} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 rounded-xl transition-colors"><Download className="w-4 h-4" />Download PDF Report</button>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div><div className="text-sm text-white/80 mb-1">Future Education Cost</div><div className="text-3xl md:text-4xl font-bold">{formatCurrency(results.futureCost)}</div></div>
                  <div><div className="text-sm text-white/80 mb-1">Monthly SIP Needed</div><div className="text-3xl md:text-4xl font-bold">{formatCurrency(results.monthlySIP)}/mo</div></div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/20 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><div className="text-xs text-white/60 mb-1">Years Left</div><div className="font-semibold">{results.yearsLeft} Years</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Current Cost</div><div className="font-semibold">{formatCurrency(currentCost * 100000)}</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Existing Grows To</div><div className="font-semibold">{formatCurrency(results.existingValue)}</div></div>
                  <div><div className="text-xs text-white/60 mb-1">Additional Needed</div><div className="font-semibold">{formatCurrency(results.amountNeeded)}</div></div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-2xl border border-amber-100 p-5 md:p-6">
                <h3 className="text-lg font-bold text-amber-900 mb-3">Education Cost Reference (India)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between p-3 bg-white rounded-lg"><span className="text-slate-600">Engineering (IIT/NIT)</span><span className="font-semibold text-slate-900">₹8-15 Lakhs</span></div>
                  <div className="flex justify-between p-3 bg-white rounded-lg"><span className="text-slate-600">Medical (MBBS)</span><span className="font-semibold text-slate-900">₹25-80 Lakhs</span></div>
                  <div className="flex justify-between p-3 bg-white rounded-lg"><span className="text-slate-600">MBA (IIMs)</span><span className="font-semibold text-slate-900">₹20-30 Lakhs</span></div>
                  <div className="flex justify-between p-3 bg-white rounded-lg"><span className="text-slate-600">Overseas (MS/MBA)</span><span className="font-semibold text-slate-900">₹40-80 Lakhs</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">Secure Your Child's Future Today</h2>
          <p className="text-slate-600 mb-6">Start a dedicated child education SIP. Our advisors help you pick the right funds and lock-in strategy.</p>
          <Link to="/sip-investments" className="inline-block bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap">Explore SIP Options</Link>
        </div>
      </section>
      <Footer /><FloatingCTA />
    </div>
  );
}